import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  Renderer2,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ProgressBar } from 'primeng/progressbar';
import { Toast } from 'primeng/toast';

import { AuthService } from '@auth/services/auth.service';
import { AudioUnlockService } from '@core/services/audio-unlock.service';
import { BreadcrumbsService } from '@core/services/breadcrumbs.service';
import { LoaderService } from '@core/services/loader.service';
import { LogService } from '@core/services/log.service';
import { ProducerIdentityService } from '@core/services/producer-identity.service';
import { ProducerService } from '@core/services/producer.service';
import { RouterService } from '@core/services/router.service';
import { EstablishmentHubSignalRService } from '@core/services/signal-r/establishment-hub/establishment-hub.signalr.service';
import { SoundService } from '@core/services/sound.service';
import { environment } from '@environment/environment';
import * as enTranslation from '@src/assets/i18n/en.json';

import { LocalSignInDialog } from './modules/auth/components/local-sign-in-dialog/local-sign-in-dialog';
import { Breadcrumbs } from './modules/core/components/breadcrumbs/breadcrumbs';
import { EditProfileModal } from './modules/core/components/edit-profile-modal/edit-profile-modal';
import { Header } from './modules/core/components/header/header';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'yt-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  imports: [Breadcrumbs, ConfirmDialog, EditProfileModal, Header, LocalSignInDialog, RouterOutlet, Toast, ProgressBar],
  providers: [ConfirmationService],
  standalone: true
})
export class App implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly logService = inject(LogService);
  private readonly loaderService = inject(LoaderService);
  private readonly producerService = inject(ProducerService);
  private readonly producerIdentityService = inject(ProducerIdentityService);
  private readonly routerService = inject(RouterService);
  private readonly breadcrumbsService = inject(BreadcrumbsService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly audioUnlockService = inject(AudioUnlockService);
  private readonly soundService = inject(SoundService);
  // Instantiated at the app root so its connection lifecycle runs app-wide (MLP-1521): it holds the producer hub
  // connection open while signed in to receive user-targeted claims-refresh pushes on any page.
  private readonly establishmentHub = inject(EstablishmentHubSignalRService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly renderer = inject(Renderer2);

  readonly headerColor = computed(() => this.routerService.snapshot()?.routeData['headerColor'] || 'dark');
  readonly hideHeader = computed(() => this.routerService.snapshot()?.routeData['hideHeader'] || false);
  readonly hideBreadcrumbs = this.breadcrumbsService.hideBreadcrumbs;
  readonly loading = this.loaderService.isLoading;

  readonly title = 'ProducersApp';

  private readonly authUser = this.authService.authUser;
  private readonly swVersionUpdates = toSignal(this.swUpdate.versionUpdates);

  constructor() {
    effect(() => {
      // AuthService owns the session and its own failures — it notifies on a failed sign-in and navigates to
      // /landing on logout — so here the app only reacts to a live identity by readying the producer: materialize
      // the facet + re-mint, then load the producer record. The route guards (producerOnboardedGuard / onboardingGuard)
      // own the status-based routing from there.
      const authUser = this.authUser();
      if (!authUser) return;
      untracked(() => void this.producerIdentityService.ensureProducerReady());
    });

    effect(() => {
      const event = this.swVersionUpdates();
      if (!event) return;

      this.logService.info('[App] SW update', { event });
      switch (event.type) {
        case 'VERSION_DETECTED':
          this.logService.info('[App] VERSION_DETECTED', { version: event.version.hash });
          break;
        case 'VERSION_READY':
          this.logService.info('[App] VERSION_READY Downloading new app version', {
            currentVersion: event.currentVersion.hash,
            newVersion: event.latestVersion.hash
          });
          this.appUpdatedDialog();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this.logService.info('[App] VERSION_INSTALLATION_FAILED', {
            version: event.version.hash,
            error: event.error
          });
          break;
      }
    });

    if (this.swUpdate.isEnabled) {
      this.setupSwUpdateCheck();
    }
  }

  ngOnInit(): void {
    this.translateService.setTranslation('en', enTranslation);
    this.translateService.setDefaultLang('en');
    this.translateService.use('en');

    this.unlockAudio();

    this.producerService.loadOnboardingUser();
    this.initSquareSDk();

    this.logService.info('[App] SW enabled', { enabled: this.swUpdate.isEnabled });
  }

  private setupSwUpdateCheck() {
    setInterval(() => {
      this.logService.info('[App] SW checking for update');
      this.swUpdate.checkForUpdate();
    }, 600000);
  }

  appUpdatedDialog(): void {
    this.confirmationService.confirm({
      accept: () => {
        this.logService.info('[App] SW update version accepted');
        // window.location.reload();
      },
      header: 'Confirmation',
      icon: 'pi pi-info-circle',
      key: 'yt-updated-dialog',
      message: 'New version available. Load New Version?'
    });
  }

  initSquareSDk() {
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.squareLink;
    this.renderer.appendChild(document.body, script);
  }

  unlockAudio() {
    const unlock = () => {
      this.audioUnlockService.unlock();
      this.soundService.notifyUnlocked();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };

    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }
}
